import { normalizarTexto } from 'src/app/helpers/busca-eventos.helper';
import type { EntityFilterDTO } from 'src/app/api/generated/v3/schemas/entityFilterDTO';

/**
 * Filtro dinâmico do ADR-0019 — a tradução entre o catálogo de `/filtros` e a
 * UI, compartilhada por todas as telas que a adotam.
 *
 * Isto NASCEU em `checkin-filtros.ts` (piloto GUE-391) e foi extraído daqui
 * quando a tela de meus eventos passou a usar a mesma toolbar. Nada aqui
 * conhece check-in nem meus eventos: são funções puras sobre
 * `EntityFilterDTO[]`. O que é de UMA tela — ordenações, sessão, rótulos de
 * domínio — fica no arquivo daquela tela.
 *
 * ## Quem manda é o catálogo
 *
 * `GET .../filtros` devolve `EntityFilterDTO[]` — `path`, `label`, `type`,
 * `condition` e, para `enumerated`, `options`. Nada é hardcoded no front: um
 * filtro novo no catálogo aparece na tela sozinho, e um que sai do catálogo
 * some. Duas traduções, mesmo catálogo: {@link filtrosParaChips} (chips da
 * toolbar) e {@link camposDeBusca} (barra de busca por prefixo, que também
 * inclui os textuais).
 *
 * ## A query é sempre `campo[operador]=valor`
 *
 * Colchetes, sem exceção. O `FiltroQueryParser` do backend só casa
 * `^(.+)\[(.+)]$` — um param solto (`?nome=ana`) não é filtro, é ignorado em
 * silêncio e a lista volta inteira. O vocabulário de operador é fechado
 * (`eq`, `ne`, `gt`, `gte`, `lt`, `lte`, `contains`); qualquer outro código é
 * 400, e um `path` fora do catálogo também.
 *
 * Note que o operador da URL NÃO é a grafia do `condition` do DTO: o catálogo
 * declara a semântica (`equals`, `greaterThanOrEqual`, `contains`) e a URL usa
 * a chave curta (`eq`, `gte`, `contains`). O mapeamento é por TIPO e vive em
 * {@link OPERADOR_TEXTO}/{@link OPERADOR_LISTA}/{@link OPERADORES_PERIODO} —
 * único lugar do front que nomeia operador.
 *
 * | `type`       | UI                     | Query                          |
 * |--------------|------------------------|--------------------------------|
 * | `enumerated` | chip com lista         | `loteId[eq]=<value da opção>`  |
 * | `date`       | chip dia/faixa         | `dataCheckin[gte]`, `[lte]`    |
 * |              | dia inteiro            | `dataCheckin[eq]=<ISO DATE>`   |
 * | `text`       | prefixo na barra       | `nome[contains]=ana`           |
 * | `numeric`, `bool` | fora — sem forma de valor ainda                   |
 *
 * ## O `label` vira o prefixo digitável
 *
 * O `value` de uma opção enumerada é opaco: o front só faz `String(value)` e
 * devolve o mesmo texto na query. Costuma ser UUID, mas não é contrato — o
 * catálogo pode emitir sentinela ("sistema") ou enum em texto na mesma lista.
 *
 * A barra de busca deriva o prefixo da primeira palavra normalizada do
 * `label` — "Data do check-in" → `data:`, "Lote" → `lote:`. Trocar um label
 * no backend TROCA o prefixo, e é assim que se muda um: não há tabela de
 * prefixo no front. Colisão de primeira palavra cai no `path`.
 */

export interface OpcaoFiltro {
  valor: string;
  rotulo: string;
}

/**
 * Chip de lista (dropdown de opções — lote, status). O de período é tratado à
 * parte ({@link ChipPeriodo}): tem dois campos e dois modos, não uma lista.
 */
export interface ChipLista {
  path: string;
  rotulo: string;
  opcoes: OpcaoFiltro[];
}

export interface ChipPeriodo {
  path: string;
  rotulo: string;
}

export interface ChipsFiltroDinamico {
  periodo: ChipPeriodo | null;
  listas: ChipLista[];
}

/**
 * Traduz o catálogo de `GET .../ingressos/filtros` (GUE-391) em chips. `type:
 * 'date'` vira o chip de período; `type: 'enumerated'` vira chip de lista.
 * `text`/`numeric`/`bool` ficam fora — nenhum filtro do piloto usa esses
 * tipos, e a tela ainda não tem um chip genérico pra eles.
 */
export function filtrosParaChips(catalogo: EntityFilterDTO[]): ChipsFiltroDinamico {
  const periodo = catalogo.find((f) => f.type === 'date');
  const listas = catalogo.filter((f) => f.type === 'enumerated');
  return {
    periodo: periodo ? { path: periodo.path, rotulo: periodo.label ?? periodo.path } : null,
    listas: listas.map((f) => ({
      path: f.path,
      rotulo: f.label ?? f.path,
      opcoes: (f.options ?? []).map((o) => ({
        valor: String(o.value),
        rotulo: o.label ?? String(o.value),
      })),
    })),
  };
}

/** Modo de precisão do chip de período — decisão 3 do ADR-0019. */
export type ModoPeriodo = 'dia' | 'horas';

/**
 * `<input type="date">` já devolve `YYYY-MM-DD` — é o formato ISO DATE que o
 * backend deriva pro dia inteiro em SP. Não precisa de conversão nenhuma.
 */
export function diaParaValorFiltro(valorInput: string): string {
  return valorInput;
}

/**
 * `<input type="datetime-local">` devolve `YYYY-MM-DDTHH:mm` (sem segundos,
 * sem offset). O fuso da operação é sempre SP (ADR-0009) — mesma convenção já
 * usada em toda a tela (`FUSO_EXIBICAO`) —, e SP não observa horário de verão
 * desde 2019, então o offset é sempre `-03:00`.
 */
export function horaParaValorFiltro(valorInput: string): string {
  return `${valorInput}:00-03:00`;
}

/** Inverso de {@link horaParaValorFiltro}, pra popular o `<input>` ao reabrir o chip. */
export function valorFiltroParaHora(valorCru: string | null): string {
  if (!valorCru) return '';
  return valorCru.replace(/:00[+-]\d{2}:\d{2}$|Z$/, '').slice(0, 16);
}

/**
 * Os dois lados do período como o formulário mostra. O dia gravado em `eq`
 * aparece nos DOIS campos: é o mesmo intervalo, e deixá-los em branco daria o
 * chip aceso com formulário vazio — filtro fantasma ao reabrir ou ao entrar
 * por deeplink.
 */
export function ladosDoPeriodo(valores: Record<string, string> | undefined): {
  gte: string;
  lte: string;
} {
  const dia = valores?.[OPERADOR_DIA];
  if (dia) return { gte: dia, lte: dia };
  return { gte: valores?.['gte'] ?? '', lte: valores?.['lte'] ?? '' };
}

/**
 * Os dois lados do período → os operadores que vão pro filtro. Lados iguais e
 * sem hora são o DIA: viram `eq`, uma chave só — equivalência pura, o backend
 * deriva o mesmo intervalo semiaberto. Um lado só continua sendo intervalo
 * aberto ("de 26 em diante"), que é justamente o que `eq` não pode roubar.
 */
export function periodoParaFiltro(
  gte: string | null,
  lte: string | null,
): Record<string, string> {
  if (gte && gte === lte && !gte.includes('T')) return { [OPERADOR_DIA]: gte };
  const porOperador: Record<string, string> = {};
  if (gte) porOperador['gte'] = gte;
  if (lte) porOperador['lte'] = lte;
  return porOperador;
}

const DIA_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const DIA_BR = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Um dia digitado na barra (`data:2026-08-26` ou `data:26/08/2026`) → ISO DATE.
 *
 * `null` enquanto não for um dia completo E válido: aí o painel mostra os
 * campos De/Até em vez da linha de aplicar. `31/02` é `null` — o round-trip
 * pelo `Date` derruba data que não existe, que `\d{2}` sozinho aceitaria.
 */
export function interpretarDia(termo: string): string | null {
  const cru = termo.trim();
  const iso = DIA_ISO.exec(cru);
  const br = DIA_BR.exec(cru);
  const partes = iso ? [iso[1], iso[2], iso[3]] : br ? [br[3], br[2], br[1]] : null;
  if (!partes) return null;
  const dia = partes.join('-');
  const data = new Date(`${dia}T00:00:00Z`);
  return !Number.isNaN(data.getTime()) && data.toISOString().slice(0, 10) === dia ? dia : null;
}

/** Um valor cru de filtro tem `T` quando é instante (faixa de horas), não dia. */
export function modoDoValor(valorCru: string | null): ModoPeriodo {
  return valorCru?.includes('T') ? 'horas' : 'dia';
}

/**
 * Filtros aplicados agora, chaveados pelo `path` do catálogo — nenhum nome de
 * filtro é hardcoded aqui, ao contrário de `EstadoCheckin` antes do GUE-391.
 * `{ dataCheckin: { gte: '2026-08-26' }, loteId: { eq: '<uuid>' } }` é o mesmo
 * shape que vira `dataCheckin[gte]=2026-08-26&loteId[eq]=<uuid>` na query e na
 * URL — estado, wire e deeplink falam o mesmo formato de propósito, pra um
 * filtro novo do catálogo não pedir mudança de código em lugar nenhum aqui.
 */
export type FiltrosDinamicos = Record<string, Record<string, string>>;

/** `{ dataCheckin: { gte: 'x', lte: 'y' } }` → `{ 'dataCheckin[gte]': 'x', 'dataCheckin[lte]': 'y' }`. */
export function achatarFiltros(filtros: FiltrosDinamicos): Record<string, string> {
  const achatado: Record<string, string> = {};
  for (const [path, porOperador] of Object.entries(filtros)) {
    for (const [operador, valor] of Object.entries(porOperador)) {
      if (valor) achatado[`${path}[${operador}]`] = valor;
    }
  }
  return achatado;
}

/**
 * Operadores de query por TIPO de filtro. É o tipo que determina o operador
 * (decisão do ADR-0019), não o nome do campo — por isso ficam aqui e não numa
 * tabela por `path`.
 *
 * Filtro textual vai em colchetes como qualquer outro (`nome[contains]=ana`):
 * o `FiltroQueryParser` do backend só casa `^(.+)\[(.+)]$` e IGNORA em
 * silêncio um param solto — `?nome=ana` não filtra nada. O vocabulário aceito
 * é fechado (`eq`, `ne`, `gte`, `lte`, `gt`, `lt`, `contains`); qualquer outro
 * código, `like` inclusive, é 400. Nos campos de texto o operador não muda a
 * semântica (o controller trata `nome[eq]` e `nome[contains]` como a mesma
 * busca parcial), e `contains` é o que o catálogo declara em `condition`.
 */
export const OPERADOR_TEXTO = 'contains';
export const OPERADOR_LISTA = 'eq';
/**
 * Dia inteiro numa chave só (`dataCheckin[eq]=2026-08-26`): mesmo intervalo
 * semiaberto que `gte`+`lte` com a data repetida. É o que dá para expressar
 * "esse dia" sem escrever a data duas vezes — um lado só continua significando
 * intervalo aberto ("de 26 em diante"), e o `eq` não rouba isso.
 */
export const OPERADOR_DIA = 'eq';
/**
 * `eq` precisa estar aqui, não só nos que a UI escreve: {@link chavesDeFiltro}
 * é o que ZERA na URL a chave que saiu de uso, e uma chave fora desta lista
 * nunca é apagada (o `queryParamsHandling: 'merge'` não toca no que não é
 * mencionado) — o dia voltaria a se aplicar sozinho no reload seguinte.
 */
export const OPERADORES_PERIODO = [OPERADOR_DIA, 'gte', 'lte'];

/** Como o campo se comporta na barra de busca: escreve, escolhe ou datas. */
export type TipoCampoBusca = 'texto' | 'lista' | 'periodo';

/**
 * Um campo do catálogo, do ponto de vista da barra de busca por prefixo.
 *
 * Unifica o que os chips veem em dois tipos separados ({@link ChipLista} e
 * {@link ChipPeriodo}) e acrescenta o que só a barra precisa: o `prefixo`
 * digitável e os `operadores` que aquele tipo usa na query.
 */
export interface CampoBusca {
  path: string;
  rotulo: string;
  /** O que se digita antes dos dois-pontos: `lote:`, `nome:`. */
  prefixo: string;
  tipo: TipoCampoBusca;
  /** Vazio fora de `lista`. */
  opcoes: OpcaoFiltro[];
  operadores: string[];
}

/**
 * A primeira PALAVRA — separada por espaço —, sem pontuação interna. O corte
 * é no espaço e não no primeiro caractere não-alfanumérico porque hífen fica
 * DENTRO da palavra em português: cortar ali transforma "E-mail" em `e:` e
 * "Check-in" em `check:`.
 */
function primeiraPalavra(valor: string): string {
  return normalizarTexto(valor).trim().split(/\s+/)[0].replace(/[^a-z0-9]/g, '');
}

/**
 * Prefixo derivado do RÓTULO, não do `path`: quem digita conhece "Lote" e
 * "Data do check-in", não `loteId` nem `dataCheckin`. A primeira palavra é o
 * que a pessoa tenta primeiro — `lote:`, `status:`, `data:`, `email:`.
 *
 * Colisão (dois rótulos começando igual) cai no `path` normalizado, que é
 * único por construção — nenhum campo fica sem prefixo e, portanto, fora da
 * barra.
 */
function prefixoDe(rotulo: string, path: string, usados: Set<string>): string {
  const doRotulo = primeiraPalavra(rotulo);
  if (doRotulo && !usados.has(doRotulo)) return doRotulo;
  const doPath = primeiraPalavra(path);
  if (doPath && !usados.has(doPath)) return doPath;
  return normalizarTexto(path).replace(/[^a-z0-9]/g, '');
}

const TIPO_DO_CATALOGO: Record<string, TipoCampoBusca> = {
  text: 'texto',
  enumerated: 'lista',
  date: 'periodo',
};

const OPERADORES_DO_TIPO: Record<TipoCampoBusca, string[]> = {
  texto: [OPERADOR_TEXTO],
  lista: [OPERADOR_LISTA],
  periodo: OPERADORES_PERIODO,
};

/**
 * Catálogo de `/filtros` → campos da barra de busca.
 *
 * Diferente de {@link filtrosParaChips}, aqui os filtros `text` ENTRAM: um
 * filtro textual não rende um chip (um dropdown só para digitar seria um
 * modal em volta de um `<input>`), mas rende um prefixo. `numeric` e `bool`
 * continuam fora — não há forma de valor para eles ainda.
 *
 * A ordem é a do catálogo: o backend decide o que aparece primeiro no menu.
 */
export function camposDeBusca(catalogo: EntityFilterDTO[]): CampoBusca[] {
  const usados = new Set<string>();
  const campos: CampoBusca[] = [];
  for (const filtro of catalogo) {
    const tipo = TIPO_DO_CATALOGO[filtro.type];
    if (!tipo) continue;
    const rotulo = filtro.label ?? filtro.path;
    const prefixo = prefixoDe(rotulo, filtro.path, usados);
    usados.add(prefixo);
    campos.push({
      path: filtro.path,
      rotulo,
      prefixo,
      tipo,
      opcoes: (filtro.options ?? []).map((o) => ({
        valor: String(o.value),
        rotulo: o.label ?? String(o.value),
      })),
      operadores: OPERADORES_DO_TIPO[tipo],
    });
  }
  return campos;
}

const EXPRESSAO_COM_PREFIXO = /^([\p{L}\p{N}_-]+):([\s\S]*)$/u;

/**
 * Lê o que está escrito na barra. `lote:cam` → o campo Lote com o termo
 * "cam"; `maria` ou `18:30` → `null`, ou seja, busca livre (`filtro`).
 *
 * Prefixo desconhecido também é `null`: quem digita `nota:` num evento cujo
 * catálogo não tem esse filtro está buscando texto, não filtrando — e vai ver
 * isso no painel, que não reconhece o prefixo.
 */
export function interpretarBusca(
  texto: string,
  campos: readonly CampoBusca[],
): { campo: CampoBusca; termo: string } | null {
  const casado = EXPRESSAO_COM_PREFIXO.exec(texto.trimStart());
  if (!casado) return null;
  const prefixo = normalizarTexto(casado[1]);
  const campo = campos.find((c) => c.prefixo === prefixo);
  return campo ? { campo, termo: casado[2].trimStart() } : null;
}

/**
 * As chaves `campo[operador]` que a URL/query PODEM ter, dado o catálogo
 * atual — cada campo contribui os operadores do seu TIPO. Usado só pra saber
 * quais chaves zerar na URL quando um filtro deixa de estar ativo (ver
 * checkin-deeplink.ts).
 */
export function chavesDeFiltro(campos: readonly CampoBusca[]): string[] {
  return campos.flatMap((campo) => campo.operadores.map((op) => `${campo.path}[${op}]`));
}
